<!-- src/pages/LandingPageSummerVacation.vue -->
<template>
  <div :dir="currentLang === 'ar' ? 'rtl' : 'ltr'" :class="currentLang === 'ar' ? 'font-arabic' : 'font-outfit'" class="min-h-screen bg-[#07090b] text-white overflow-x-hidden text-start">
    <!-- ===== HEADER / NAVBAR ===== -->
    <header class="sticky top-0 z-50 bg-[#07090b]/85 backdrop-blur-md border-b border-white/10">
      <!-- Top Support Bar -->
      <div class="border-b border-white/5 bg-[#030506]/60 py-2 text-[11px] md:text-xs">
        <div class="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6">
          <div class="flex items-center gap-4">
            <span class="flex items-center gap-1.5 opacity-80">
              <span class="text-amber-400">📞</span> {{ t('nav.support') }}: +973 3214 1440
            </span>
            <span class="hidden items-center gap-1.5 opacity-80 sm:flex">
              <span class="text-amber-400">✉️</span> support@vehitraworld.com
            </span>
          </div>
          <div class="flex items-center gap-6">
            <span class="flex items-center gap-1.5 opacity-80">
              <span class="text-amber-400">🌍</span> {{ t('nav.worldwide') }}
            </span>
            
            <!-- Reactive Language Toggle Button -->
            <button @click="toggleLang" class="flex items-center gap-1.5 opacity-85 hover:opacity-100 hover:text-amber-400 focus:outline-none bg-transparent border-none font-semibold text-[11px] md:text-xs cursor-pointer transition">
              <span>{{ currentLang === 'ar' ? '🇬🇧' : '🇧🇭' }}</span>
              <span>{{ currentLang === 'ar' ? 'English' : 'العربية' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Main Header Bar -->
      <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 md:px-6">
        <!-- Logo -->
        <div class="flex items-center gap-3">
          <div class="bg-white rounded-xl p-2 shadow-lg flex items-center justify-center">
            <img :src="vwLogo" alt="Vehitra World" class="h-9 w-auto object-contain cursor-pointer" @click="scrollToTop" />
          </div>
        </div>

        <!-- Navigation Links -->
        <nav class="hidden lg:flex items-center gap-8 text-sm font-medium tracking-wide">
          <a href="#services" class="hover:text-amber-400 transition">{{ t('nav.airport') }}</a>
          <a href="#services" class="hover:text-amber-400 transition">{{ t('nav.chauffeur') }}</a>
          <a href="#destinations" class="hover:text-amber-400 transition">{{ t('nav.destinations') }}</a>
          <a href="#family" class="hover:text-amber-400 transition">{{ t('nav.meet') }}</a>
          <a href="#family" class="hover:text-amber-400 transition">{{ t('nav.vehicles') }}</a>
          <a href="#footer" class="hover:text-amber-400 transition">{{ t('nav.about') }}</a>
        </nav>

        <!-- Book Now Button -->
        <div>
          <button @click="scrollToForm" class="rounded-full bg-amber-500 hover:bg-amber-400 px-6 py-2.5 text-xs md:text-sm font-bold text-[#07090b] uppercase tracking-wider transition shadow-lg shadow-amber-500/20 active:scale-95">
            {{ t('nav.bookNow') }}
          </button>
        </div>
      </div>
    </header>

    <!-- ===== HERO SECTION WITH FORM ===== -->
    <section class="relative min-h-[90vh] flex items-center py-10 md:py-16 bg-cover bg-center" :style="{ backgroundImage: `linear-gradient(rgba(7,9,11,0.7), rgba(7,9,11,0.85)), url(${heroImage})` }">
      <div class="mx-auto max-w-7xl w-full px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        
        <!-- Left Column: Copy -->
        <div class="lg:col-span-7 flex flex-col justify-center text-start">
          <span class="mb-4 self-start inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold text-amber-400 ring-1 ring-white/15">
            🌴 {{ t('hero.campaign') }}
          </span>

          <h1 class="leading-[1.1] font-extrabold tracking-tight">
            <span class="block text-4xl md:text-6xl text-white">{{ t('hero.title1') }}</span>
            <span class="block text-5xl md:text-7xl text-amber-400 mt-1">{{ t('hero.title2') }}</span>
            <span class="block text-4xl md:text-6xl text-white mt-1">{{ t('hero.title3') }}</span>
          </h1>

          <div class="mt-6 max-w-xl leading-relaxed text-zinc-300">
            <p class="text-base md:text-lg font-bold text-white">
              {{ t('hero.description1') }}
            </p>
            <p class="mt-2 text-sm md:text-base font-normal opacity-85">
              {{ t('hero.description2') }}
            </p>
          </div>

          <!-- CTA Buttons -->
          <div class="mt-8 flex flex-wrap gap-4">
            <button @click="scrollToForm" class="inline-flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-400 px-8 py-4 text-sm md:text-base font-bold text-[#07090b] transition shadow-lg shadow-amber-500/30 active:scale-95">
              {{ t('hero.getQuote') }} <span class="mx-2" :class="currentLang === 'ar' ? 'rotate-180 inline-block' : ''">→</span>
            </button>
            <a :href="whatsappUrl" target="_blank" class="inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-500 px-8 py-4 text-sm md:text-base font-bold text-white transition active:scale-95">
              {{ t('hero.whatsapp') }}
            </a>
          </div>

          <!-- GCC Features Badges -->
          <div class="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-white/10 pt-8">
            <div class="flex items-center gap-2.5">
              <svg class="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 18.75V7.5a.75.75 0 011.13-.65l3.87 2.325 3.19-4.786a.75.75 0 011.24 0l3.19 4.786 3.87-2.325a.75.75 0 011.13.65v11.25M3 18.75h18M3 18.75a2.25 2.25 0 002.25 2.25h13.5a2.25 2.25 0 002.25-2.25" />
              </svg>
              <span class="text-xs font-medium text-zinc-300">{{ t('hero.gcc') }}</span>
            </div>
            <div class="flex items-center gap-2.5">
              <svg class="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12a8.25 8.25 0 0116.5 0v5.25a2.25 2.25 0 01-2.25 2.25h-1.5a2.25 2.25 0 01-2.25-2.25V15a2.25 2.25 0 012.25-2.25h3m-16.5 0h3A2.25 2.25 0 019 15v2.25a2.25 2.25 0 01-2.25 2.25h-1.5A2.25 2.25 0 013 17.25V12z" />
              </svg>
              <span class="text-xs font-medium text-zinc-300">{{ t('hero.support') }}</span>
            </div>
            <div class="flex items-center gap-2.5">
              <svg class="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10 21l1.5-2.5L10 12l6 1.5 2-1.5-1.5-1.5-6.5-.5L9 5l-1.5-1.5-6.5 7l-2-1L3.5 8.5l4 3.5-3 5H6l2.5-3.5L10 21z" />
              </svg>
              <span class="text-xs font-medium text-zinc-300">{{ t('hero.airportTransfers') }}</span>
            </div>
            <div class="flex items-center gap-2.5">
              <svg class="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 18.75a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM15.75 18.75a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 14.25v-2.25a2.25 2.25 0 012.25-2.25h9.75L17.25 6h3.75a.75.75 0 01.75.75v7.5M3 14.25h19v2.25H3v-2.25z" />
              </svg>
              <span class="text-xs font-medium text-zinc-300">{{ t('hero.familyVehicles') }}</span>
            </div>
            <div class="flex items-center gap-2.5">
              <svg class="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-xs font-medium text-zinc-300">{{ t('hero.fastConfirmation') }}</span>
            </div>
            <div class="flex items-center gap-2.5">
              <svg class="h-5 w-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              <span class="text-xs font-medium text-zinc-300">{{ t('hero.safeService') }}</span>
            </div>
          </div>
        </div>

        <!-- Right Column: Glassmorphic Form Card -->
        <div id="quote-form-section" class="lg:col-span-5 w-full">
          <div class="lead-card">
            <h2 class="text-xl md:text-2xl font-bold tracking-tight text-white">{{ t('form.title') }}</h2>
            <p class="mt-1 text-xs text-zinc-400">{{ t('form.subtitle') }}</p>

            <form @submit.prevent="submitLead" class="lead-form mt-6 flex flex-col gap-4">
              <!-- Service Type Dropdown -->
              <div>
                <label class="mb-1 block text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">{{ t('form.serviceType') }}</label>
                <select v-model="form.serviceType" class="form-input select-arrow">
                  <option value="" disabled>{{ t('form.selectService') }}</option>
                  <option value="Airport Transfer">{{ t('form.airport') }}</option>
                  <option value="Chauffeur Service">{{ t('form.chauffeur') }}</option>
                  <option value="Car Rental">{{ t('form.carRental') }}</option>
                  <option value="Meet & Greet">{{ t('form.meetGreet') }}</option>
                  <option value="Bus">{{ t('form.bus') }}</option>
                </select>
              </div>

              <!-- Pick-up Location -->
              <div class="field-reset min-w-0">
                <LocationField
                  class="field-reset af-loc"
                  :layout="'simple'"
                  :drive="'distance'"
                  :hasError="false"
                  :data="pickUp"
                  type="pickUp"
                  :label="t('form.pickup')"
                  @addressSelected="addr => onAddressSelected(addr, 'pickUp')"
                />
              </div>

              <!-- Drop-off Location -->
              <div class="field-reset min-w-0">
                <LocationField
                  class="field-reset af-loc"
                  :layout="'simple'"
                  :drive="'distance'"
                  :hasError="false"
                  :data="dropOff"
                  type="dropOff"
                  :label="t('form.dropoff')"
                  @addressSelected="addr => onAddressSelected(addr, 'dropOff')"
                />
              </div>

              <!-- Date / Time Grid -->
              <div class="grid grid-cols-2 gap-4">
                <div class="field-reset">
                  <DateField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.date"
                    :label="t('form.date')"
                    @dateChanged="onDateChanged"
                  />
                </div>
                <div class="field-reset">
                  <TimeField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.time"
                    :label="t('form.time')"
                    @timeChanged="onTimeChanged"
                  />
                </div>
              </div>

              <!-- Passengers & Name Grid -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="mb-1 block text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">{{ t('form.passengers') }}</label>
                  <select v-model="form.passengers" class="form-input select-arrow">
                    <option value="" disabled>{{ t('form.selectPassengers') }}</option>
                    <option value="1">1 {{ currentLang === 'ar' ? 'راكب' : t('form.paxCount') }}</option>
                    <option value="2">2 {{ currentLang === 'ar' ? 'ركاب' : t('form.paxCounts') }}</option>
                    <option value="3">3 {{ currentLang === 'ar' ? 'ركاب' : t('form.paxCounts') }}</option>
                    <option value="4">4 {{ currentLang === 'ar' ? 'ركاب' : t('form.paxCounts') }}</option>
                    <option value="5">5 {{ currentLang === 'ar' ? 'ركاب' : t('form.paxCounts') }}</option>
                    <option value="6">6 {{ currentLang === 'ar' ? 'ركاب' : t('form.paxCounts') }}</option>
                    <option value="7">7+ {{ currentLang === 'ar' ? 'ركاب' : t('form.paxCounts') }}</option>
                  </select>
                </div>
                <div>
                  <label class="mb-1 block text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">{{ t('form.name') }}</label>
                  <input type="text" class="form-input" :placeholder="t('form.namePlaceholder')" v-model="form.name" />
                </div>
              </div>

              <!-- Mobile/WhatsApp number -->
              <div>
                <label class="mb-1 block text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">{{ t('form.phone') }}</label>
                <input type="text" class="form-input" placeholder="e.g. +973 3214 1440" v-model="form.phone" />
              </div>

              <!-- Email Address -->
              <div>
                <label class="mb-1 block text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">{{ t('form.email') }}</label>
                <input type="email" class="form-input" :placeholder="t('form.emailPlaceholder')" v-model="form.email" />
              </div>

              <!-- Submit button -->
              <div class="mt-2">
                <button type="submit" :disabled="submitting" class="btn-primary">
                  {{ submitting ? t('form.submitting') : t('form.submit') }}
                </button>
                <div class="mt-3 flex items-center justify-center gap-4 text-[10px] text-zinc-400 uppercase tracking-wider">
                  <span class="flex items-center gap-1"><span class="text-amber-500">✔</span> {{ t('form.noHidden') }}</span>
                  <span class="flex items-center gap-1"><span class="text-amber-500">✔</span> {{ t('form.instantConf') }}</span>
                </div>
              </div>
            </form>
          </div>
        </div>

      </div>
    </section>

    <!-- ===== OUR SERVICES ===== -->
    <section id="services" class="py-20 bg-[#0b0e11] border-y border-white/5">
      <div class="mx-auto max-w-7xl px-4 md:px-6">
        <div class="text-center mb-16">
          <span class="text-xs font-bold text-amber-500 uppercase tracking-widest">{{ t('services.tag') }}</span>
          <h2 class="mt-2 text-3xl md:text-4xl font-extrabold text-white">{{ t('services.title') }}</h2>
          <div class="mt-3 mx-auto w-16 h-1 bg-amber-500 rounded-full"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <div v-for="svc in localizedServices" :key="svc.title" class="service-card group">
            <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-400 group-hover:bg-amber-500 group-hover:text-[#07090b] transition-all duration-300 p-2.5">
              <!-- Dynamic SVG based on service title -->
              <svg v-if="svc.title === t('services.airport.title')" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10 21l1.5-2.5L10 12l6 1.5 2-1.5-1.5-1.5-6.5-.5L9 5l-1.5-1.5-6.5 7l-2-1L3.5 8.5l4 3.5-3 5H6l2.5-3.5L10 21z" />
              </svg>
              
              <svg v-else-if="svc.title === t('services.chauffeur.title')" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="3" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v6M12 15v6M3 12h6M15 12h6" />
              </svg>
              
              <svg v-else-if="svc.title === t('services.meet.title')" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.05 13.05a3.536 3.536 0 00-5 0l-1 1a3.536 3.536 0 005 5l1-1a3.536 3.536 0 000-5M13.95 10.95a3.536 3.536 0 005 0l1-1a3.536 3.536 0 00-5-5l-1 1a3.536 3.536 0 000 5M10 14l4-4" />
              </svg>
              
              <svg v-else-if="svc.title === t('services.family.title')" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 18.75a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM15.75 18.75a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 14.25v-2.25a2.25 2.25 0 012.25-2.25h9.75L17.25 6h3.75a.75.75 0 01.75.75v7.5M3 14.25h19v2.25H3v-2.25z" />
              </svg>
              
              <svg v-else-if="svc.title === t('services.support.title')" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.6 9h16.8M3.6 15h16.8" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.5 3a17 17 0 000 18M12.5 3a17 17 0 000 18" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">{{ svc.title }}</h3>
            <p class="text-sm text-zinc-400 leading-relaxed">{{ svc.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== POPULAR DESTINATIONS ===== -->
    <section id="destinations" class="py-20 bg-[#07090b]">
      <div class="mx-auto max-w-7xl px-4 md:px-6">
        <div class="text-center mb-16">
          <span class="text-xs font-bold text-amber-500 uppercase tracking-widest">{{ t('destinations.tag') }}</span>
          <h2 class="mt-2 text-3xl md:text-5xl font-extrabold text-white">
            {{ t('destinations.title1') }}<span class="text-amber-400 italic">{{ t('destinations.title2') }}</span>{{ t('destinations.title3') }}
          </h2>
          <div class="mt-3 mx-auto w-16 h-1 bg-amber-500 rounded-full"></div>
        </div>

        <!-- Horizontal scroll / Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          <div v-for="dest in destinations" :key="dest.name" @click="selectDestination(dest.name)" class="destination-card group">
            <div class="relative h-64 overflow-hidden">
              <!-- Image -->
              <img :src="dest.image" :alt="dest.name" class="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <!-- Gradient Overlay -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent"></div>
              <!-- Content -->
              <div class="absolute bottom-4 left-4 right-4 text-start">
                <h3 class="text-lg font-bold text-white tracking-wide">{{ currentLang === 'ar' ? dest.arName : dest.name }}</h3>
                <span class="mt-1.5 inline-flex items-center text-[10px] font-bold text-amber-400 uppercase tracking-wider group-hover:text-white transition-colors">
                  {{ t('destinations.book') }} <span class="mx-1" :class="currentLang === 'ar' ? 'rotate-180 inline-block' : ''">→</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-12 text-center">
          <button @click="scrollToForm" class="rounded-full border border-white/20 hover:border-amber-500 hover:text-amber-400 px-8 py-3 text-xs md:text-sm font-bold uppercase tracking-wider transition">
            {{ t('destinations.viewAll') }}
          </button>
        </div>
      </div>
    </section>

    <!-- ===== FAMILY TRAVEL CALLOUT ===== -->
    <section id="family" class="py-16 bg-[#07090b]">
      <div class="mx-auto max-w-7xl px-4 md:px-6">
        <div class="bg-white rounded-[32px] p-6 md:p-8 shadow-2xl border border-zinc-100 text-zinc-900 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <!-- Left: Image of Family -->
          <div class="lg:col-span-3 rounded-2xl overflow-hidden shadow-md">
            <img :src="familyTripImg" alt="Family Vacation Group" class="w-full h-full object-cover aspect-[4/3] lg:aspect-auto" />
          </div>

          <!-- Center: Text & Benefits -->
          <div class="lg:col-span-5 text-start flex flex-col justify-center">
            <span class="text-[10px] md:text-xs font-extrabold text-zinc-800 uppercase tracking-widest">
              {{ t('family.tag') }}
            </span>
            <h2 class="mt-2 text-2xl md:text-3xl font-extrabold text-zinc-900 leading-tight">
              {{ t('family.title') }}<span class="text-amber-500">{{ t('family.easier') }}</span>
            </h2>

            <!-- Horizontal Cards Row -->
            <div class="mt-6 flex items-center justify-between gap-1.5 overflow-x-auto py-1 select-none">
              <!-- Card 1: Child-Friendly Options -->
              <div class="flex flex-col items-center justify-center p-2 bg-white border border-zinc-200 rounded-xl w-[78px] h-[82px] shadow-sm flex-shrink-0">
                <svg class="h-6 w-6 text-zinc-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 14a4 4 0 100-8 4 4 0 000 8z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 2v4M7 6c0-2 2-3 5-3s5 1 5 3M6.5 17.5a6 6 0 0111 0" />
                </svg>
                <span class="mt-1 text-[8px] font-bold text-zinc-800 leading-tight text-center" v-html="t('family.child')"></span>
              </div>

              <span class="text-zinc-400 font-bold text-sm flex-shrink-0">+</span>

              <!-- Card 2: Family-Sized Vehicles -->
              <div class="flex flex-col items-center justify-center p-2 bg-white border border-zinc-200 rounded-xl w-[78px] h-[82px] shadow-sm flex-shrink-0">
                <svg class="h-6 w-6 text-zinc-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 16a2 2 0 100-4 2 2 0 000 4z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 16a2 2 0 100-4 2 2 0 000 4z" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2 13h20v-3a2 2 0 00-2-2H4a2 2 0 00-2 2v3zM6 8V6a1 1 0 011-1h10a1 1 0 011 1v2" />
                </svg>
                <span class="mt-1 text-[8px] font-bold text-zinc-800 leading-tight text-center" v-html="t('family.family')"></span>
              </div>

              <span class="text-zinc-400 font-bold text-sm flex-shrink-0">+</span>

              <!-- Card 3: Airport Assistance -->
              <div class="flex flex-col items-center justify-center p-2 bg-white border border-zinc-200 rounded-xl w-[78px] h-[82px] shadow-sm flex-shrink-0">
                <svg class="h-6 w-6 text-zinc-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <circle cx="12" cy="6" r="3" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 20v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 10l3-3 3 3" />
                </svg>
                <span class="mt-1 text-[8px] font-bold text-zinc-800 leading-tight text-center" v-html="t('family.airport')"></span>
              </div>

              <span class="text-zinc-400 font-bold text-sm flex-shrink-0">+</span>

              <!-- Card 4: Luggage Support -->
              <div class="flex flex-col items-center justify-center p-2 bg-white border border-zinc-200 rounded-xl w-[78px] h-[82px] shadow-sm flex-shrink-0">
                <svg class="h-6 w-6 text-zinc-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <rect x="5" y="7" width="14" height="12" rx="1.5" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3M9 11v4M15 11v4" />
                </svg>
                <span class="mt-1 text-[8px] font-bold text-zinc-800 leading-tight text-center" v-html="t('family.luggage')"></span>
              </div>

              <span class="text-zinc-400 font-bold text-sm flex-shrink-0">+</span>

              <!-- Card 5: 24/7 Customer Care -->
              <div class="flex flex-col items-center justify-center p-2 bg-white border border-zinc-200 rounded-xl w-[78px] h-[82px] shadow-sm flex-shrink-0">
                <svg class="h-6 w-6 text-zinc-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 12c0-4.97 4.03-9 9-9s9 4.03 9 9v7a2 2 0 01-2 2h-3a2 2 0 01-2-2v-3a2 2 0 012-2h3M3 12h3a2 2 0 012 2v3a2 2 0 01-2 2H3M19 16v1a2 2 0 01-2 2h-2" />
                </svg>
                <span class="mt-1 text-[8px] font-bold text-zinc-800 leading-tight text-center" v-html="t('family.care')"></span>
              </div>
            </div>

            <!-- Description -->
            <p class="mt-6 text-zinc-600 text-xs md:text-sm leading-relaxed">
              {{ t('family.desc') }}
            </p>

            <!-- CTA Button -->
            <div class="mt-6">
              <button @click="selectVehicleType('Van')" class="rounded-md bg-[#030506] hover:bg-amber-500 text-white hover:text-[#030506] px-7 py-3.5 text-xs font-bold uppercase tracking-wider transition duration-300 flex items-center gap-2 shadow-md">
                {{ t('family.btn') }} <span :class="currentLang === 'ar' ? 'rotate-180 inline-block' : ''">→</span>
              </button>
            </div>
          </div>

          <!-- Right: Mercedes V-Class Showcase -->
          <div class="lg:col-span-4 flex items-center justify-center">
            <img :src="vClassImg" alt="Mercedes V-Class" class="w-full h-auto max-h-[260px] object-contain hover:scale-105 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TESTIMONIALS SLIDER ===== -->
    <section class="py-20 bg-[#07090b]">
      <div class="mx-auto max-w-7xl px-4 md:px-6">
        <div class="text-center mb-16">
          <span class="text-xs font-bold text-amber-500 uppercase tracking-widest">{{ t('testimonials.tag') }}</span>
          <h2 class="mt-2 text-3xl md:text-4xl font-extrabold text-white">{{ t('testimonials.title') }}</h2>
          <div class="mt-3 mx-auto w-16 h-1 bg-amber-500 rounded-full"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div v-for="t in localizedTestimonials" :key="t.author" class="testimonial-card relative">
            <span class="absolute top-6 right-8 text-6xl text-amber-500/10 font-serif">“</span>
            <div class="mb-4 text-amber-400">★★★★★</div>
            <p class="text-sm md:text-base text-zinc-300 italic leading-relaxed relative z-10">“{{ t.text }}”</p>
            <div class="mt-6 border-t border-white/5 pt-4">
              <h4 class="font-bold text-white">{{ t.author }}</h4>
              <p class="text-xs text-amber-500/80 font-semibold mt-0.5">{{ t.location }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== BOTTOM CAMPAIGN BANNER ===== -->
    <section class="relative py-24 bg-cover bg-center border-t border-white/10" :style="{ backgroundImage: `linear-gradient(rgba(7,9,11,0.85), rgba(7,9,11,0.9)), url(${bannerImage})` }">
      <div class="mx-auto max-w-4xl px-4 text-center">
        <h2 class="text-3xl md:text-5xl font-extrabold leading-tight text-white">
          {{ t('lowerBanner.title1') }} <span class="text-amber-400">{{ t('lowerBanner.title2') }}</span>
        </h2>
        <p class="mx-auto mt-4 max-w-xl text-sm md:text-base text-zinc-300 leading-relaxed">
          {{ t('lowerBanner.desc') }}
        </p>

        <!-- CTA Buttons -->
        <div class="mt-8 flex flex-wrap gap-4 justify-center">
          <button @click="scrollToForm" class="rounded-full bg-amber-500 hover:bg-amber-400 px-8 py-4 text-sm md:text-base font-bold text-[#07090b] transition shadow-lg shadow-amber-500/30 active:scale-95">
            {{ t('lowerBanner.quote') }} <span class="mx-2" :class="currentLang === 'ar' ? 'rotate-180 inline-block' : ''">→</span>
          </button>
          <a :href="whatsappUrl" target="_blank" class="rounded-full bg-emerald-600 hover:bg-emerald-500 px-8 py-4 text-sm md:text-base font-bold text-white transition active:scale-95">
            {{ t('lowerBanner.whatsapp') }}
          </a>
        </div>

        <!-- Trust Stats -->
        <div class="mt-16 grid grid-cols-2 md:grid-cols-6 gap-4 border-t border-white/10 pt-10 text-center">
          <div>
            <div class="text-xl md:text-2xl font-bold text-amber-400">100+</div>
            <div class="text-[10px] text-zinc-400 mt-1 uppercase font-medium">{{ currentLang === 'ar' ? 'دولة وأكثر' : 'Countries Covered' }}</div>
          </div>
          <div>
            <div class="text-xl md:text-2xl font-bold text-amber-400">10,000+</div>
            <div class="text-[10px] text-zinc-400 mt-1 uppercase font-medium">{{ currentLang === 'ar' ? 'عميل سعيد' : 'Happy Customers' }}</div>
          </div>
          <div>
            <div class="text-xl md:text-2xl font-bold text-amber-400">500+</div>
            <div class="text-[10px] text-zinc-400 mt-1 uppercase font-medium">{{ currentLang === 'ar' ? 'مدينة حول العالم' : 'Cities Worldwide' }}</div>
          </div>
          <div>
            <div class="text-xl md:text-2xl font-bold text-amber-400">24/7</div>
            <div class="text-[10px] text-zinc-400 mt-1 uppercase font-medium">{{ currentLang === 'ar' ? 'دعم مستمر' : 'Customer Support' }}</div>
          </div>
          <div>
            <div class="text-xl md:text-2xl font-bold text-amber-400">100%</div>
            <div class="text-[10px] text-zinc-400 mt-1 uppercase font-medium">{{ currentLang === 'ar' ? 'رضا مضمون' : 'Satisfaction Guaranteed' }}</div>
          </div>
          <div>
            <div class="text-xl md:text-2xl font-bold text-amber-400">{{ currentLang === 'ar' ? 'آمن' : 'Secure' }}</div>
            <div class="text-[10px] text-zinc-400 mt-1 uppercase font-medium">{{ currentLang === 'ar' ? 'حجز سهل وموثوق' : '& Easy Booking' }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FOOTER ===== -->
    <footer id="footer" class="bg-[#030506] border-t border-white/10 pt-16 pb-8 text-start">
      <div class="mx-auto max-w-7xl px-4 md:px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <!-- Col 1: About -->
        <div>
          <div class="inline-flex bg-white rounded-xl p-1.5 shadow-md items-center justify-center">
            <img :src="vwLogo" alt="Vehitra World" class="h-9 w-auto object-contain" />
          </div>
          <p class="mt-4 text-sm text-zinc-400 leading-relaxed">
            {{ t('footer.desc') }}
          </p>
        </div>

        <!-- Col 2: Quick Links -->
        <div>
          <h4 class="text-sm font-bold text-white uppercase tracking-wider mb-4">{{ t('footer.quickLinks') }}</h4>
          <ul class="space-y-2.5 text-sm text-zinc-400">
            <li><a href="#services" class="hover:text-amber-400 transition">{{ t('nav.airport') }}</a></li>
            <li><a href="#services" class="hover:text-amber-400 transition">{{ t('nav.chauffeur') }}</a></li>
            <li><a href="#destinations" class="hover:text-amber-400 transition">{{ t('nav.destinations') }}</a></li>
            <li><a href="#family" class="hover:text-amber-400 transition">{{ t('nav.meet') }}</a></li>
            <li><a href="#family" class="hover:text-amber-400 transition">{{ t('nav.vehicles') }}</a></li>
            <li><a href="#footer" class="hover:text-amber-400 transition">{{ t('nav.about') }}</a></li>
          </ul>
        </div>

        <!-- Col 3: Destinations -->
        <div>
          <h4 class="text-sm font-bold text-white uppercase tracking-wider mb-4">{{ t('footer.topDestinations') }}</h4>
          <ul class="space-y-2.5 text-sm text-zinc-400">
            <li><a href="#destinations" @click="selectDestination('Paris')" class="hover:text-amber-400 transition">{{ currentLang === 'ar' ? 'باريس، فرنسا' : 'Paris, France' }}</a></li>
            <li><a href="#destinations" @click="selectDestination('London')" class="hover:text-amber-400 transition">{{ currentLang === 'ar' ? 'لندن، المملكة المتحدة' : 'London, United Kingdom' }}</a></li>
            <li><a href="#destinations" @click="selectDestination('Switzerland')" class="hover:text-amber-400 transition">{{ currentLang === 'ar' ? 'سويسرا، جبال الألب' : 'Switzerland, Alps' }}</a></li>
            <li><a href="#destinations" @click="selectDestination('Istanbul')" class="hover:text-amber-400 transition">{{ currentLang === 'ar' ? 'إسطنبول، تركيا' : 'Istanbul, Turkey' }}</a></li>
            <li><a href="#destinations" @click="selectDestination('Rome')" class="hover:text-amber-400 transition">{{ currentLang === 'ar' ? 'روما، إيطاليا' : 'Rome, Italy' }}</a></li>
            <li><a href="#destinations" @click="selectDestination('Maldives')" class="hover:text-amber-400 transition">{{ currentLang === 'ar' ? 'جزر المالديف' : 'Maldives Islands' }}</a></li>
          </ul>
        </div>

        <!-- Col 4: Contact -->
        <div>
          <h4 class="text-sm font-bold text-white uppercase tracking-wider mb-4">{{ t('footer.contactUs') }}</h4>
          <ul class="space-y-3 text-sm text-zinc-400">
            <li class="flex items-start gap-2">
              <span class="text-amber-400">📞</span>
              <span>+973 3214 1440</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-amber-400">✉️</span>
              <span class="break-all">support@vehitraworld.com</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-amber-400">📍</span>
              <span>{{ currentLang === 'ar' ? 'البحرين / دعم عالمي' : 'Bahrain / Worldwide Support' }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="mx-auto max-w-7xl px-4 md:px-6 mt-16 pt-8 border-t border-white/5 text-center text-xs text-zinc-500">
        <p>{{ t('footer.rights') }}</p>
      </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { reactive, ref, defineComponent, h, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter } from 'vue-router'
import LocationField from '@/components/search/LocationField.vue'
import DateField from '@/components/search/DateField.vue'
import TimeField from '@/components/search/TimeField.vue'
import { getBackendBaseUrl } from '@/utils/common'

// Local Assets
import vwLogo from '@/assets/logo.png'
import vClassImg from '@/assets/cars/van.png'
import familyTripImg from '@/assets/landing/summer/family.png'

// Generated Campaign Images
import heroImageUrl from '@/assets/landing/summer/hero.png'
import bannerImageUrl from '@/assets/landing/summer/banner.png'

// Local Destination Images
import parisImg from '@/assets/landing/summer/paris.png'
import londonImg from '@/assets/landing/summer/london.png'
import switzerlandImg from '@/assets/landing/summer/switzerland.png'
import istanbulImg from '@/assets/landing/summer/istanbul.png'
import romeImg from '@/assets/landing/summer/rome.png'
import bangkokImg from '@/assets/landing/summer/bangkok.png'
import maldivesImg from '@/assets/landing/summer/maldives.png'

const heroImage = heroImageUrl
const bannerImage = bannerImageUrl

const router = useRouter()
const submitting = ref(false)

// Localization state & dictionary helper
const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return 'en'
  const navLang = navigator.language || (navigator as any).userLanguage || 'en'
  return navLang.toLowerCase().startsWith('ar') ? 'ar' : 'en'
}

const currentLang = ref(localStorage.getItem('preferredLang') || detectBrowserLanguage())

const updateSEOMeta = () => {
  // Title
  document.title = currentLang.value === 'ar'
    ? 'انتقالات المطار الفاخرة وخدمات السائق الخاص لعطلة الصيف — فيترا وورلد'
    : 'Premium Summer Vacation Airport Transfers & Chauffeur Services — Vehitra World'

  // Description
  let metaDesc = document.querySelector('meta[name="description"]')
  if (!metaDesc) {
    metaDesc = document.createElement('meta')
    metaDesc.setAttribute('name', 'description')
    document.head.appendChild(metaDesc)
  }
  metaDesc.setAttribute('content', currentLang.value === 'ar'
    ? 'احجز انتقالات مطار ممتازة وخدمة سائق خاص في جميع أنحاء العالم لعطلتك الصيفية مع فيترا وورلد.'
    : 'Book premium airport transfers and chauffeur services worldwide for your summer vacation with Vehitra World.')

  // Keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]')
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta')
    metaKeywords.setAttribute('name', 'keywords')
    document.head.appendChild(metaKeywords)
  }
  metaKeywords.setAttribute('content', currentLang.value === 'ar'
    ? 'انتقالات المطار، سائق خاص، عطلة الصيف، حجز سيارة، نقل عائلي، فيترا وورلد'
    : 'airport transfers, chauffeur service, summer vacation, car booking, family transfer, Vehitra World')
}

const toggleLang = () => {
  currentLang.value = currentLang.value === 'en' ? 'ar' : 'en'
  localStorage.setItem('preferredLang', currentLang.value)
  updateSEOMeta()
}

onMounted(() => {
  updateSEOMeta()
})

const t = (key: string) => {
  const keys = key.split('.')
  let res: any = translations[currentLang.value]
  for (const k of keys) {
    if (res && res[k]) {
      res = res[k]
    } else {
      return key
    }
  }
  return res
}

const form = reactive({
  name: '',
  phone: '',
  email: '',
  pickup: '',
  dropoff: '',
  datetime: '',
  notes: '',
  serviceType: '',
  passengers: '',
  carType: '',
})

const pickUp = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
  date: null as string | null,
  time: '' as string
})

const dropOff = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } }
})

// WhatsApp Integration
const WHATSAPP_NUMBER = '97332141440'
const whatsappMessage = `Hi Vehitra World,
I would like to enquire about your premium transportation services, including airport transfers, chauffeur services, and private travel solutions for my upcoming trip.`
const whatsappUrl = computed(() => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`)

// Form event handlers
const onAddressSelected = (address: any, type: 'pickUp' | 'dropOff') => {
  const add = address ?? { id: '', name: '', coordinates: { lat: null, lng: null } }
  if (type === 'pickUp') {
    pickUp.location = add
    form.pickup = add?.name || add?.description || ''
  } else {
    dropOff.location = add
    form.dropoff = add?.name || add?.description || ''
  }
}

const onDateChanged = (d: string) => {
  pickUp.date = d
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}

const onTimeChanged = (t: string) => {
  pickUp.time = t
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}

const selectDestination = (name: string) => {
  dropOff.location = { id: '', name: name, isAirport: false, coordinates: { lat: null, lng: null } }
  form.dropoff = name
  scrollToForm()
}

const selectVehicleType = (type: string) => {
  form.carType = type
  scrollToForm()
}

const scrollToForm = () => {
  const el = document.getElementById('quote-form-section')
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Submission
const submitLead = async () => {
  if (!form.serviceType || !form.pickup || !form.dropoff || !form.datetime || !form.passengers || !form.name || !form.phone) {
    alert(currentLang.value === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة المميزة بنجمة *.' : 'Please fill in all required fields marked with *.')
    return
  }

  const API_BASE = getBackendBaseUrl()
  const payload = {
    name: form.name,
    phone: form.phone,
    email: form.email || null,
    pickup: form.pickup,
    dropoff: form.dropoff,
    datetime: form.datetime,
    notes: `Service Type: ${form.serviceType}\n` + (form.notes ? `User Notes: ${form.notes}` : ''),
    source: 'summer-vacation',
    car_type: form.carType || null,
    passengers: Number(form.passengers) || 0,
    suitcases: 0,
    pickUp,
    dropOff,
  }

  submitting.value = true
  try {
    const res = await fetch(`${API_BASE}api/leads/airport-transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const msg = await res.text().catch(() => '')
      throw new Error(`Request failed (${res.status}) ${msg}`)
    }
    router.push({ name: 'ThankYou' })
  } catch (e: any) {
    alert(e?.message || (currentLang.value === 'ar' ? 'حدث خطأ ما أثناء إرسال طلبك.' : 'Something went wrong while submitting your request.'))
  } finally {
    submitting.value = false
  }
}

// Computeds for dynamic locale changes
const localizedServices = computed(() => [
  { title: t('services.airport.title'), description: t('services.airport.desc') },
  { title: t('services.chauffeur.title'), description: t('services.chauffeur.desc') },
  { title: t('services.meet.title'), description: t('services.meet.desc') },
  { title: t('services.family.title'), description: t('services.family.desc') },
  { title: t('services.support.title'), description: t('services.support.desc') }
])

const localizedTestimonials = computed(() => [
  { text: t('testimonials.t1'), author: currentLang.value === 'ar' ? 'عائشة السويدي' : 'Aisha Al Suwaidi', location: currentLang.value === 'ar' ? 'دبي، الإمارات' : 'Dubai, UAE' },
  { text: t('testimonials.t2'), author: currentLang.value === 'ar' ? 'خالد آل ثاني' : 'Khalid Al Thani', location: currentLang.value === 'ar' ? 'الدوحة، قطر' : 'Doha, Qatar' },
  { text: t('testimonials.t3'), author: currentLang.value === 'ar' ? 'فاطمة الحامد' : 'Fatima Al Hamed', location: currentLang.value === 'ar' ? 'البحرين' : 'Bahrain' }
])

// Destinations List Data with localized names
const destinations = [
  { name: 'Paris', arName: 'باريس', image: parisImg },
  { name: 'London', arName: 'لندن', image: londonImg },
  { name: 'Switzerland', arName: 'سويسرا', image: switzerlandImg },
  { name: 'Istanbul', arName: 'إسطنبول', image: istanbulImg },
  { name: 'Rome', arName: 'روما', image: romeImg },
  { name: 'Bangkok', arName: 'بانكوك', image: bangkokImg },
  { name: 'Maldives', arName: 'المالديف', image: maldivesImg },
]

const familyFeatures = [
  { icon: '👶', label: 'Child-Friendly Options (Baby Car Seats)' },
  { icon: '🚐', label: 'Family-Sized Premium Vehicles' },
  { icon: '🛎️', label: 'Airport Assistance & Meet & Greet' },
  { icon: '💼', label: 'Luggage Load & Unload Support' },
  { icon: '☎️', label: '24/7 Priority Customer Care' },
]

const translations: Record<string, any> = {
  en: {
    nav: {
      support: '24/7 Support',
      worldwide: 'We Serve Worldwide',
      airport: 'Airport Transfers',
      chauffeur: 'Chauffeur Services',
      destinations: 'Destinations',
      meet: 'Meet & Greet',
      vehicles: 'Vehicles',
      about: 'About Us',
      bookNow: 'Book Now'
    },
    hero: {
      campaign: 'Summer Campaign 2026',
      title1: 'Your Summer',
      title2: 'Vacation',
      title3: 'Starts Here',
      description1: 'Premium Airport Transfers, Chauffeur Services, Car Rental, Meet & Greet & Buses & Coaches Worldwide.',
      description2: 'From airport pickup and private chauffeur services to car rentals, meet & greet assistance, and group transportation, Vehitra World ensures your summer vacation starts smoothly, comfortably, and stress-free wherever you travel.',
      getQuote: 'GET MY QUOTE',
      whatsapp: 'WHATSAPP NOW',
      gcc: 'Designed For GCC Travelers',
      support: '24/7 Customer Support',
      airportTransfers: 'Worldwide Airport Transfers',
      familyVehicles: 'Family Friendly Vehicles',
      fastConfirmation: 'Fast Booking Confirmation',
      safeService: 'Safe & Reliable Service'
    },
    form: {
      title: 'GET YOUR TRAVEL QUOTE',
      subtitle: 'Fast · Reliable · Worldwide',
      serviceType: 'Service Type*',
      selectService: 'Select Service',
      airport: 'Airport Transfer',
      chauffeur: 'Chauffeur Service / Private Tour',
      carRental: 'Car Rental',
      meetGreet: 'Meet & Greet',
      bus: 'Bus / Coach Group',
      pickup: 'Pick-up Location*',
      dropoff: 'Drop-off Location*',
      date: 'Pick-up Date*',
      time: 'Pick-up Time*',
      passengers: 'Passengers*',
      selectPassengers: 'Select Passengers',
      paxCount: 'Passenger',
      paxCounts: 'Passengers',
      name: 'Your Name*',
      namePlaceholder: 'Enter your name',
      phone: 'WhatsApp / Phone Number*',
      email: 'Email Address (Optional)',
      emailPlaceholder: 'Enter your email address',
      submit: 'GET INSTANT QUOTE',
      submitting: 'Submitting Request…',
      noHidden: 'No Hidden Charges',
      instantConf: 'Instant Confirmation'
    },
    services: {
      tag: 'Our Services',
      title: 'Everything You Need For A Stress-Free Vacation',
      airport: { title: 'Airport Transfers', desc: 'Reliable airport pickup and drop-off in 100+ cities worldwide.' },
      chauffeur: { title: 'Private Chauffeur', desc: 'Professional chauffeurs for business, leisure, and city tours.' },
      meet: { title: 'Meet & Greet', desc: 'Personalized greeting and assistance at the arrivals gate.' },
      family: { title: 'Family Transport', desc: 'Spacious vehicles for families and groups of all sizes.' },
      support: { title: 'Worldwide Support', desc: "We're with you at every step of your journey across the globe." }
    },
    destinations: {
      tag: 'Popular Destinations for GCC Travelers',
      title1: 'Where Will Your Next ',
      title2: 'Summer Adventure',
      title3: ' Be?',
      book: 'Book Now',
      viewAll: 'View All Destinations'
    },
    family: {
      tag: 'TRAVEL TOGETHER. TRAVEL BETTER.',
      title: 'We Make Every Journey ',
      easier: 'Easier',
      desc: 'From kids to grandparents, we ensure a comfortable, safe and stress-free ride for your entire family.',
      child: 'Child-Friendly<br/>Options',
      family: 'Family-Sized<br/>Vehicles',
      airport: 'Airport<br/>Assistance',
      luggage: 'Luggage<br/>Support',
      care: '24/7 Customer<br/>Care',
      btn: 'BOOK FAMILY TRANSFER'
    },
    testimonials: {
      tag: 'Trusted By GCC Travelers',
      title: 'What Our Guests Say',
      t1: 'Booked our Paris airport transfer through Vehitra World. Everything was smooth from arrival to hotel. Highly recommended!',
      t2: 'Excellent chauffeur service during our Switzerland family trip. Very professional, safe driver and strictly on time.',
      t3: 'Best travel experience in Istanbul. The driver was waiting for us and the ride was extremely comfortable.'
    },
    lowerBanner: {
      title1: 'Ready for Your',
      title2: 'Next Adventure?',
      desc: 'Book your premium airport transfer or private chauffeur service today with Vehitra World and travel in complete comfort.',
      quote: 'GET MY QUOTE',
      whatsapp: 'WHATSAPP NOW'
    },
    footer: {
      desc: 'Your trusted travel partner for airport transfers, chauffeur services, and private transportation worldwide.',
      quickLinks: 'Quick Links',
      topDestinations: 'Top Destinations',
      contactUs: 'Contact Us',
      rights: '© 2026 Vehitra World · All Rights Reserved · Privacy Policy · Terms & Conditions'
    }
  },
  ar: {
    nav: {
      support: 'الدعم على مدار الساعة',
      worldwide: 'نخدم جميع أنحاء العالم',
      airport: 'انتقالات المطار',
      chauffeur: 'خدمات السائق الخاص',
      destinations: 'الوجهات',
      meet: 'الاستقبال والمساعدة',
      vehicles: 'السيارات',
      about: 'من نحن',
      bookNow: 'احجز الآن'
    },
    hero: {
      campaign: 'حملة الصيف ٢٠٢٦',
      title1: 'عطلتك الصيفية',
      title2: 'تبدأ',
      title3: 'من هنا',
      description1: 'انتقالات المطار المتميزة، خدمات السائق الخاص، تأجير السيارات، الاستقبال والمساعدة، والحافلات والسيارات الكبيرة حول العالم.',
      description2: 'من الاستقبال في المطار وخدمات السائق الخاص إلى تأجير السيارات والمساعدة في الاستقبال والنقل الجماعي، تضمن لك فيترا وورلد أن تبدأ عطلتك الصيفية بسلاسة وراحة وخلو من الإجهاد أينما كنت مسافراً.',
      getQuote: 'احصل على عرض سعر',
      whatsapp: 'تواصل عبر الواتساب',
      gcc: 'مصمم للمسافرين الخليجيين',
      support: 'دعم العملاء على مدار الساعة',
      airportTransfers: 'انتقالات المطار حول العالم',
      familyVehicles: 'سيارات مناسبة للعائلات',
      fastConfirmation: 'تأكيد حجز سريع',
      safeService: 'خدمة آمنة وموثوقة'
    },
    form: {
      title: 'احصل على عرض سعر السفر',
      subtitle: 'سريع · موثوق · عالمي',
      serviceType: 'نوع الخدمة*',
      selectService: 'اختر الخدمة',
      airport: 'نقل المطار',
      chauffeur: 'خدمة السائق الخاص / جولة خاصة',
      carRental: 'تأجير سيارات',
      meetGreet: 'الاستقبال والمساعدة',
      bus: 'حافلة / باص',
      pickup: 'مكان الاستلام*',
      dropoff: 'مكان التوصيل*',
      date: 'تاريخ الاستلام*',
      time: 'وقت الاستلام*',
      passengers: 'الركاب*',
      selectPassengers: 'اختر عدد الركاب',
      paxCount: 'رافر',
      paxCounts: 'ركاب',
      name: 'الاسم*',
      namePlaceholder: 'أدخل اسمك',
      phone: 'رقم الواتساب / الهاتف*',
      email: 'البريد الإلكتروني (اختياري)',
      emailPlaceholder: 'أدخل البريد الإلكتروني الخاص بك',
      submit: 'احصل على عرض سعر فوري',
      submitting: 'جاري إرسال الطلب…',
      noHidden: 'لا توجد رسوم خفية',
      instantConf: 'تأكيد فوري'
    },
    services: {
      tag: 'خدماتنا',
      title: 'كل ما تحتاجه لعطلة صيفية خالية من القلق',
      airport: { title: 'انتقالات المطار', desc: 'نقل موثوق من وإلى المطار في أكثر من ١٠٠ مدينة حول العالم.' },
      chauffeur: { title: 'سائق خاص', desc: 'سائقون محترفون للأعمال والترفيه والجولات السياحية في المدينة.' },
      meet: { title: 'الاستقبال والمساعدة', desc: 'استقبال شخصي ومساعدة عند بوابة الوصول بالمطار.' },
      family: { title: 'نقل العائلات', desc: 'سيارات واسعة ومريحة للعائلات والمجموعات بجميع الأحجام.' },
      support: { title: 'دعم عالمي', desc: 'نحن معك في كل خطوة من رحلتك في جميع أنحاء العالم.' }
    },
    destinations: {
      tag: 'الوجهات المفضلة للمسافرين الخليجيين',
      title1: 'أين ستكون مغامرتك ',
      title2: 'الصيفية القادمة',
      title3: '؟',
      book: 'احجز الآن',
      viewAll: 'عرض جميع الوجهات'
    },
    family: {
      tag: 'سافروا معاً، لتجربة أفضل.',
      title: 'نجعل كل رحلة ',
      easier: 'أسهل',
      desc: 'من الأطفال إلى الأجداد، نضمن رحلة مريحة وآمنة وخالية من القلق لجميع أفراد عائلتك.',
      child: 'خيارات مناسبة<br/>للأطفال',
      family: 'سيارات بحجم<br/>عائلي',
      airport: 'مساعدة في<br/>المطار',
      luggage: 'مساعدة في<br/>الأمتعة',
      care: 'رعاية العملاء<br/>على مدار الساعة',
      btn: 'احجز نقل عائلي'
    },
    testimonials: {
      tag: 'موثوق من المسافرين الخليجيين',
      title: 'ماذا يقول ضيوفنا',
      t1: 'حجزنا انتقالات المطار في باريس عبر فيترا وورلد. كان كل شيء سهلاً ومريحاً من لحظة وصولنا حتى الفندق. ننصح بهم بشدة!',
      t2: 'خدمة سائق ممتازة خلال رحلتنا العائلية إلى سويسرا. سائق محترف وآمن وملتزم بالوقت تماماً.',
      t3: 'أفضل تجربة سفر في إسطنبول. كان السائق في انتظارنا وكانت الرحلة مريحة للغاية.'
    },
    lowerBanner: {
      title1: 'هل أنت مستعد لـ',
      title2: 'مغامرتك القادمة؟',
      desc: 'احجز نقل المطار المميز أو خدمة السائق الخاص اليوم مع فيترا وورلد وسافر براحة تامة.',
      quote: 'احصل على عرض سعر',
      whatsapp: 'تواصل عبر الواتساب'
    },
    footer: {
      desc: 'شريكك الموثوق للسفر لانتقالات المطار، خدمات السائق الخاص، والنقل الخاص في جميع أنحاء العالم.',
      quickLinks: 'روابط سريعة',
      topDestinations: 'أشهر الوجهات',
      contactUs: 'اتصل بنا',
      rights: '© ٢٠٢٦ فيترا وورلد · جميع الحقوق محفوظة · سياسة الخصوصية · الشروط والأحكام'
    }
  }
}

/* ---------- Google Places PAC dropdown styling patch ---------- */
function usePatchPacDropdown() {
  const getFocusedInput = (): HTMLInputElement | null =>
    document.activeElement?.tagName === 'INPUT'
      ? (document.activeElement as HTMLInputElement)
      : (document.querySelector('.lead-form input:focus') as HTMLInputElement | null)

  const apply = () => {
    const pacs = document.querySelectorAll<HTMLElement>('.pac-container')
    if (!pacs.length) return
    const input = getFocusedInput() || (document.querySelector('.lead-form input') as HTMLInputElement | null)
    if (!input) return
    const r = input.getBoundingClientRect()
    const desiredWidth = Math.max(r.width + 40, 500)
    pacs.forEach(pac => {
      pac.style.setProperty('position', 'fixed', 'important')
      pac.style.setProperty('left', `${r.left}px`, 'important')
      pac.style.setProperty('top', `${r.bottom}px`, 'important')
      pac.style.setProperty('width', `${desiredWidth}px`, 'important')
      pac.style.setProperty('minWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxHeight', '400px', 'important')
      pac.style.setProperty('overflowY', 'auto', 'important')
      pac.style.setProperty('overflowX', 'hidden', 'important')
      pac.style.setProperty('zIndex', '99999', 'important')
      pac.style.setProperty('borderRadius', '16px', 'important')
      pac.style.setProperty('border', '1px solid rgba(255,255,255,.12)', 'important')
      pac.style.setProperty('background', 'rgba(11,14,17,.98)', 'important')
      pac.style.setProperty('boxShadow', '0 20px 50px rgba(0,0,0,.6)', 'important')
      pac.style.setProperty('padding', '6px 0', 'important')
    })
  }

  const onFocusIn  = () => setTimeout(() => { apply() }, 50)
  const onResize   = () => apply()
  const onScroll   = () => apply()

  onMounted(() => {
    document.addEventListener('focusin', onFocusIn, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    const mo = new MutationObserver(apply)
    mo.observe(document.body, { childList: true, subtree: true })
  })
  onBeforeUnmount(() => {
    document.removeEventListener('focusin', onFocusIn, true)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll, true)
  })
}
usePatchPacDropdown()
</script>

<style scoped>
:global(:root) {
  --input-height: 52px;
  --input-border: rgba(255,255,255,.12);
  --input-fill: rgba(255,255,255,.05);
}

.lead-card {
  @apply rounded-3xl backdrop-blur-3xl p-6 md:p-8 text-start;
  background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03));
  border: 1px solid rgba(255,255,255,.12);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 25px 60px rgba(0,0,0,.45);
}

.form-input {
  @apply w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-zinc-500 transition duration-200 focus:border-amber-500/80 focus:bg-white/10;
  height: var(--input-height);
}

.form-input option {
  background-color: #07090b;
  color: #ffffff;
}

.form-input option:disabled {
  color: #71717a;
}

.select-arrow {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 14px;
}

[dir="rtl"] .select-arrow {
  background-position: left 16px center;
}

.btn-primary {
  @apply w-full rounded-full font-bold transition px-6 py-4 text-[#07090b] bg-amber-500 hover:bg-amber-400 disabled:opacity-60 uppercase tracking-wider text-sm shadow-lg shadow-amber-500/20 active:scale-[0.98];
}

/* ===== Locations inputs resets ===== */
.lead-form :deep(.af-loc.relative) { padding: 0 !important; overflow: visible !important; }
.lead-form :deep(.af-loc .flex.flex-col.space-y-2) { gap: 4px !important; }
.lead-form :deep(.af-loc .text-small),
.lead-form :deep(.af-loc .text-small *),
.lead-form :deep(.af-loc [class*="uppercase"]) {
  text-transform: none !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
  letter-spacing: .02em !important;
  color: rgba(255,255,255,.50) !important;
  font-weight: 600 !important;
}
.lead-form :deep(.af-loc input[type="text"]) {
  height: var(--input-height) !important;
  min-height: var(--input-height) !important;
  border-radius: 12px !important;
  border: 1px solid var(--input-border) !important;
  background: var(--input-fill) !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  outline: none !important; box-shadow: none !important;
  transition: all 0.2s;
}
.lead-form :deep(.af-loc input[type="text"]:focus) {
  border-color: rgba(245, 158, 11, 0.8) !important;
  background: rgba(255,255,255,.10) !important;
}
.lead-form :deep(.af-loc .clear-btn) { display: none !important; }

/* ===== Date/Time inputs resets ===== */
.lead-form :deep(.af-dt), 
.lead-form :deep(.af-dt [class*="p-"]), 
.lead-form :deep(.af-dt [class*="px-"]), 
.lead-form :deep(.af-dt [class*="py-"]), 
.lead-form :deep(.af-dt [class*="m-"]) {
  padding: 0 !important; margin: 0 !important;
}
.lead-form :deep(.af-dt label), 
.lead-form :deep(.af-dt .label), 
.lead-form :deep(.af-dt [class*="uppercase"]) {
  text-transform: none !important; margin-bottom: 4px !important;
  font-size: 11px !important; line-height: 1.2 !important; letter-spacing: .02em !important;
  color: rgba(255,255,255,.50) !important; font-weight: 600 !important;
}
.lead-form :deep(.af-dt input), 
.lead-form :deep(.af-dt input[type="text"]), 
.lead-form :deep(.af-dt input[type="date"]), 
.lead-form :deep(.af-dt input[type="time"]) {
  width: 100% !important; height: var(--input-height) !important; min-height: var(--input-height) !important;
  border-radius: 12px !important; border: 1px solid var(--input-border) !important; background: var(--input-fill) !important;
  color: #fff !important; font-size: 14px !important; font-weight: 500 !important; padding: 0 16px !important;
  outline: none !important; box-shadow: none !important; color-scheme: dark;
  transition: all 0.2s;
}
.lead-form :deep(.af-dt input:focus) {
  border-color: rgba(245, 158, 11, 0.8) !important;
  background: rgba(255,255,255,.10) !important;
}
.lead-form :deep(.af-dt input[type="date"]::-webkit-calendar-picker-indicator),
.lead-form :deep(.af-dt input[type="time"]::-webkit-calendar-picker-indicator) { filter: invert(1) opacity(.85); }
.lead-form :deep(.af-dt.relative) { overflow: visible !important; }

/* Custom elements styling */
.service-card {
  @apply rounded-3xl bg-[#07090b] border border-white/5 p-6 hover:border-amber-500/30 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/5 text-start;
}

.destination-card {
  @apply overflow-hidden rounded-2xl border border-white/10 cursor-pointer shadow-lg transition-all duration-300;
}

.testimonial-card {
  @apply rounded-3xl border border-white/5 bg-[#0b0e11] p-8 text-start hover:border-amber-500/25 transition-all duration-300;
}

.social-icon {
  @apply flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-zinc-400 hover:bg-amber-500 hover:text-[#07090b] transition duration-200 font-semibold;
}

.why-card {
  @apply rounded-2xl bg-white/[0.02] border border-white/5 p-6;
}
</style>

<style>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Tajawal:wght@400;500;700;800&display=swap');

.font-arabic {
  font-family: 'Tajawal', 'Outfit', sans-serif !important;
}
.font-outfit {
  font-family: 'Outfit', sans-serif !important;
}

.pac-item { color: #d8dee9 !important; font-size: 14px !important; line-height: 1.4 !important;
  padding: 10px 16px !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
.pac-item:hover { background: rgba(255,255,255,.06) !important; }
.pac-item-query { font-weight: 700 !important; color: #fff !important; }
.pac-icon { filter: brightness(0) invert(1) !important; margin-right: 8px !important; }
</style>
